// หาวัสดุด้วย AI — ตัวช่วยฝั่งหน้าเว็บ (สัญญาระหว่างส่วน: docs/ai-materials-plan.md)
//   ไฟล์นี้ไม่คุยกับฐานข้อมูล ใช้ได้ทั้งโหมด MOCK และ Node (ทดสอบ / dossier-xlsx)
//   กติกาที่ต้องคงไว้: ผล AI เป็นร่างเสมอ คนกดยืนยันก่อนเขียนลงสินค้า · ไม่แตะ hs_code · ไม่โชว์ error ดิบให้พนักงาน
import { hasThai } from './naming.js';

// ค่าโครงสร้าง = มีหลายส่วน/มีเปอร์เซ็นต์ — แตกเป็นชิปด้วยจุลภาคไม่ได้
//   ("ตัวเสื้อ: ฝ้าย 66%, โพลีเอสเตอร์ 34%; ริบ: ฝ้าย 95%" แตกแล้วกด ✕ = ส่วนประกอบเพี้ยนบนใบสำแดง)
//   จุลภาคติดเลข (70,100D · 12,500mm) ก็เหมือนกัน — แตกเป็นชิปแล้วกดอะไรสักอย่าง ค่าจะกลายเป็น "70, 100D"
export const isStructuredMaterial = (v) => /[;:%]|\([^)]*,[^)]*\)|\d,\d/.test(String(v ?? ''));

// ค่าที่คนพิมพ์เองที่หน้าตรวจผล (AI หาแหล่งไม่เจอ) — มีงานอ้างอิงแต่ไม่มีลิงก์แหล่งข้อมูล
export const typedByPerson = (c) => !!c?.material_research_id && !String(c?.material_source_url ?? '').trim();

// ── ป้ายภาษาไทย — ป้ายสีต้องมีคำกำกับเสมอ ไม่พึ่งสีอย่างเดียว ─────────────────────
export const TIER_LABEL = {
  official_primary: 'เว็บทางการของแบรนด์',
  official_regional: 'เว็บทางการของแบรนด์ (ประเทศอื่น)',
  official_document: 'เอกสารทางการของแบรนด์',
  authorized_retailer: 'ร้านค้าตัวแทน',
  marketplace: 'มาร์เก็ตเพลส (เบาะแสเท่านั้น)',
};
// tone = คลาส .hschip: ok เขียว · miss เหลือง · dg แดง
export const TIER_TONE = {
  official_primary: 'ok', official_regional: 'ok',
  official_document: 'miss', authorized_retailer: 'miss',
  marketplace: 'dg',
};
export const CONFIDENCE_LABEL = { high: 'มั่นใจสูง', medium: 'มั่นใจปานกลาง', low: 'มั่นใจต่ำ' };
export const CONFIDENCE_TONE = { high: 'ok', medium: 'miss', low: 'dg' };
export const VERDICT_LABEL = {
  confirmed: 'ตรงกับหน้าเว็บ',
  mismatch: 'มีจุดไม่ตรง',
  unreachable: 'เปิดหน้าเว็บไม่ได้',
};
export const VERDICT_TONE = { confirmed: 'ok', mismatch: 'dg', unreachable: 'miss' };
export const STATUS_LABEL = {
  queued: 'รอคิว',
  researching: 'กำลังค้น',
  researched: 'ค้นแล้ว รอตรวจซ้ำ',
  verifying: 'กำลังตรวจซ้ำ',
  draft: 'รอคนตรวจ',
  not_found: 'หาไม่เจอ',
  approved: 'ยืนยันแล้ว',
  rejected: 'ไม่ใช้',
  error: 'ขัดข้อง',
};

// ส่วนในหน้าตรวจผล — ลำดับตามงานที่คนต้องทำ
export const SECTIONS = [
  { id: 'review', label: 'รอตรวจ', statuses: ['draft', 'not_found'] },
  { id: 'active', label: 'กำลังค้น', statuses: ['queued', 'researching', 'researched', 'verifying'] },
  { id: 'done', label: 'ยืนยันแล้ว-ไม่ใช้', statuses: ['approved', 'rejected'] },
  { id: 'error', label: 'ขัดข้อง', statuses: ['error'] },
];
export const ACTIVE_STATUSES = SECTIONS[1].statuses;

const t = (v) => String(v ?? '').replace(/^[\s\u200B]+|[\s\u200B]+$/g, '');   // ตัด zero-width ด้วย ให้ตรงกับ _material_text ฝั่ง SQL
const norm = (v) => t(v).replace(/\s+/g, ' ');

// ใบนี้มีวัสดุจริงไหม — ช่องว่าง/ขึ้นบรรทัดล้วนนับเป็นไม่มี (ช่องข้อความกด Enter เพิ่มบรรทัดได้)
//   ⚠ ด่าน "ครบพร้อมส่ง broker" ทุกจุดใช้ตัวนี้ตัวเดียว: db.cardGaps · Dossier ready/missing/GapPanel
export const hasMaterial = (card) => t(card?.material) !== '';

// ข้อความจากระบบที่อาจเป็นภาษาอังกฤษดิบ (error จาก API / paused_reason) — โชว์เฉพาะที่เป็นภาษาไทย ไม่งั้นใช้คำแทน
export const thaiOr = (v, fallback) => (hasThai(v) ? t(v) : fallback);

// ผลจากโมเดลเป็น jsonb — ช่องที่หน้าจอพิมพ์ตรง ๆ ต้องเป็นข้อความ (ออบเจกต์ใน JSX = React โยน แล้วทั้งหน้าขาว)
const asText = (v) => (v == null || typeof v === 'string' ? v : typeof v === 'object' ? JSON.stringify(v) : String(v));
const asTexts = (v) => (Array.isArray(v) ? v.map(asText) : v);
export function safeResearchRow(row) {
  if (!row) return row;
  const out = { ...row, flags: asTexts(row.flags), hs_warnings: asTexts(row.hs_warnings), notes_th: asText(row.notes_th), error: asText(row.error) };
  if (row.input && typeof row.input === 'object') out.input = { ...row.input, product_names: asTexts(row.input.product_names) };
  if (row.result && typeof row.result === 'object') {
    out.result = { ...row.result, product_name_on_page: asText(row.result.product_name_on_page), variant_note: asText(row.result.variant_note), tried: asTexts(row.result.tried) };
  }
  if (row.verify && typeof row.verify === 'object') {
    out.verify = { ...row.verify, evidence: asText(row.verify.evidence), issues: asTexts(row.verify.issues), checked_urls: asTexts(row.verify.checked_urls) };
  }
  return out;
}

export const variantsDiffer = (row) => row?.result?.variants_differ === true || row?.result?.variants_differ === 'true';
export const triedList = (row) => (Array.isArray(row?.result?.tried) ? row.result.tried.filter(Boolean) : []);
export const verifyInfo = (row) => ({
  urls: Array.isArray(row?.verify?.checked_urls) ? row.verify.checked_urls.filter(Boolean) : [],
  evidence: t(row?.verify?.evidence),
  issues: Array.isArray(row?.verify?.issues) ? row.verify.issues.filter(Boolean) : [],
});

// ลิงก์จาก AI เป็นข้อมูลจากภายนอก — https เท่านั้น (javascript: / http: / มี user:pass ในลิงก์ = ไม่ทำเป็นลิงก์)
//   บัญชีนี้เปิดได้ทุกแอพของบริษัท คลิกเดียวรันสคริปต์ได้ถ้าไม่กัน
export function safeHref(url) {
  const s = t(url);
  if (!s) return null;
  try {
    const u = new URL(s);
    return u.protocol === 'https:' && !u.username && !u.password ? u.href : null;
  } catch { return null; }
}

// เวลาไทยเสมอ — ห้าม slice ISO (เพี้ยน 7 ชม. + วันเลื่อน)
export function fmtBkk(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' });
}

export function fmtUsd(v) {
  const n = Number(v);
  if (v == null || v === '' || !Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

// ── ยืนยันหลายแถวพร้อมกัน ────────────────────────────────────────────────────
// ผ่านครบทุกข้อเท่านั้น ที่เหลือคนต้องเปิดดูทีละแถว (มีค่าที่ตรวจมือแล้ว 504 แถว ห้ามถูกทับโดยไม่มีใครเห็น)
//   ⚠ "เว็บทางการ" ดูทั้ง tier และ source_on_registry — tier โมเดลเป็นคนบอกเอง หน้าเว็บปลอมบอกตัวเองว่าทางการได้
//   คืนเหตุผลภาษาไทยด้วย จะได้บอกคนว่าทำไมแถวนี้ติ๊กไม่ได้
const BULK_TIERS = new Set(['official_primary', 'official_regional']);
export function bulkBlockers(row, currentBySku = {}) {
  const out = [];
  if (row?.status !== 'draft') out.push('ยังไม่ได้อยู่ในขั้นรอคนตรวจ');
  if (!t(row?.material_th)) out.push('ยังไม่มีค่าวัสดุภาษาไทย');
  if (row?.confidence !== 'high') out.push('ความมั่นใจยังไม่สูง');
  if (row?.verify_verdict !== 'confirmed') out.push('ตรวจซ้ำแล้วยังไม่ตรงกับหน้าเว็บ');
  if (!BULK_TIERS.has(row?.source_tier)) out.push('แหล่งข้อมูลไม่ใช่เว็บทางการ');
  else if (row?.source_on_registry !== true) out.push('เว็บนี้ไม่อยู่ในทะเบียนของแบรนด์');
  if ((row?.flags ?? []).length) out.push('มีธงเตือน');
  if ((row?.hs_warnings ?? []).length) out.push('มีเรื่องพิกัดที่ควรดู');
  if (variantsDiffer(row)) out.push('สีในรุ่นนี้วัสดุไม่เท่ากัน');
  if ((row?.late_skus ?? []).length) out.push('มี SKU ที่ AI ไม่ได้ดู');
  const skus = row?.skus ?? [];
  if (!skus.length) out.push('ไม่มี SKU ในงานนี้');
  const want = norm(row?.material_th);
  let unknown = false, differs = false;
  for (const sku of skus) {
    const card = currentBySku[sku];
    if (!card) { unknown = true; continue; }
    const cur = norm(card.material);
    if (cur && cur !== want) differs = true;
  }
  if (unknown) out.push('มี SKU ที่หาใบสินค้าไม่เจอ');
  if (differs) out.push('มีค่าเดิมที่ต่างจากผลใหม่');
  return out;
}
export const canBulkApprove = (row, currentBySku = {}) => bulkBlockers(row, currentBySku).length === 0;

// "ตอนนี้ในระบบเขียนว่าอะไร?" — แยกตามค่าที่ต่างกันพร้อมจำนวน SKU · differs = ต่างจากผลใหม่ (ไฮไลต์)
//   เรียงให้ค่าที่จะโดนทับขึ้นก่อน คนจะได้เห็นก่อนกดยืนยัน
export function groupCurrentValues(skus, currentBySku = {}, nextValue = '') {
  const groups = new Map();
  for (const sku of skus ?? []) {
    const card = currentBySku[sku];
    const value = card ? norm(card.material) : null;
    const key = value == null ? '\u0000missing' : value;
    if (!groups.has(key)) groups.set(key, { value, skus: [], missing: value == null, empty: value === '' });
    groups.get(key).skus.push(sku);
  }
  const want = norm(nextValue);
  const list = [...groups.values()].map(g => ({
    ...g,
    differs: !g.missing && !g.empty && !!want && g.value !== want,
  }));
  return list.sort((a, b) => (b.differs - a.differs) || (b.skus.length - a.skus.length));
}

// ค่าที่จะเขียนจริง + อาร์กิวเมนต์ที่ส่งให้เซิร์ฟเวอร์ — ทุกทางยืนยันทีละแถวคำนวณจากตรงนี้ที่เดียว
//   edit = { th, en } ที่กำลังแก้อยู่ · ไม่มี = ยืนยันตามผล AI
//   ไม่ได้แก้อะไรเลย = ยืนยันตามผล AI · แก้ไทยอย่างเดียว = ไม่ส่งอังกฤษ (เซิร์ฟเวอร์ล้างให้ — กันค่า AI ประกบค่าที่คนแก้)
export function approvePlan(row, edit) {
  if (!edit) return { args: {}, value: t(row?.material_th) };
  const th = t(edit.th), en = t(edit.en);
  if (row?.status === 'not_found') return { args: { materialTh: th, ...(en ? { materialEn: en } : {}) }, value: th };
  const thChanged = th !== t(row?.material_th), enChanged = en !== t(row?.material_en);
  if (!thChanged && !enChanged) return { args: {}, value: th };
  if (thChanged) return { args: { materialTh: th, ...(enChanged ? { materialEn: en } : {}) }, value: th };
  return { args: { materialEn: en }, value: th };
}

// ค่าเดิมของใบนี้จะถูกทับไหม ถ้าเขียน value ลงไป — ว่างอยู่หรือตรงกันอยู่แล้ว = ไม่ทับ
export const wouldOverwrite = (card, value) => {
  const cur = norm(card?.material), want = norm(value);
  return !!cur && !!want && cur !== want;
};
// SKU ที่จะถูกทับจริง พร้อมค่าเดิม — ภาพนี้คือสิ่งที่คนต้องเห็นก่อนกดยืนยัน
export const overwriteList = (targets, currentBySku = {}, value = '') => (targets ?? [])
  .filter(sku => wouldOverwrite(currentBySku[sku], value))
  .map(sku => ({ sku, value: norm(currentBySku[sku].material) }));
// กุญแจของภาพนั้น — ค่าที่จะเขียนหรือค่าเดิมเปลี่ยนไปจากที่เห็น = กุญแจไม่ตรง = ห้ามเขียน ต้องถามใหม่
export const overwriteKey = (value, list) => JSON.stringify([
  norm(value), [...(list ?? [])].sort((a, b) => a.sku.localeCompare(b.sku)).map(x => [x.sku, x.value]),
]);

// งานที่ถูกเลื่อนเวลา (ระบบ AI ขัดข้องชั่วคราว) — ห้ามโชว์แค่ "รอคิว" / "รอตรวจซ้ำ" ไม่งั้นงานที่วนซ้ำเป็นชั่วโมงจะมองไม่เห็น
//   ขั้นตรวจถอยกลับไป researched ไม่ใช่ queued (index.ts) จึงต้องดูทั้งสองสถานะ
export const retryAt = (row, now = Date.now()) => {
  if ((row?.status !== 'queued' && row?.status !== 'researched') || !row?.next_attempt_at) return null;
  const at = Date.parse(row.next_attempt_at);
  return Number.isFinite(at) && at > now ? row.next_attempt_at : null;
};

// คืน Map: id แถวเก่า → id แถวล่าสุดของรุ่นเดียวกัน (ไว้พาคนไปดูแถวที่ถูกต้อง) · counts = แถวแบบไหนนับเป็น "แถวใหม่กว่า"
function newerMap(rows, counts) {
  const key = (r) => `${norm(r.brand).toLowerCase()}|${norm(r.model_code).toLowerCase()}`;
  const newest = new Map();
  for (const r of rows) {
    if (!counts(r)) continue;
    const at = Date.parse(r.requested_at) || 0;
    if (at > (newest.get(key(r))?.at ?? -1)) newest.set(key(r), { at, id: r.id });
  }
  const out = new Map();
  for (const r of rows) {
    const n = newest.get(key(r));
    if (n && (Date.parse(r.requested_at) || 0) < n.at) out.set(r.id, n.id);
  }
  return out;
}
// ยืนยันแถวรอตรวจ: มีแถวใหม่กว่าที่ไม่ใช่ไม่ใช้ = เซิร์ฟเวอร์ไม่รับ (approve_material_research) ไม่ต้องโชว์ปุ่มให้กดแล้วเจอ error
export const supersededMap = (rows = []) => newerMap(rows, (r) => r.status !== 'rejected');
// ใช้ผลที่ยืนยันแล้วกับ SKU เพิ่ม: เซิร์ฟเวอร์กันเฉพาะแถวที่ยืนยันใหม่กว่า (apply_material_research) — แถวใหม่กว่าที่ล้ม/หาไม่เจอ/ยังค้น
//   ยังไม่มีค่าไปทับ ถ้าซ่อนตัวเลือกตรงนี้ SKU ที่หน้าต่างลงคิวส่งมาจะไม่มีทางไปต่อ
export const newerApprovedMap = (rows = []) => newerMap(rows, (r) => r.status === 'approved');
export const supersededIds = (rows = []) => new Set(supersededMap(rows).keys());

// ค่าก่อนถูกเขียนทับ (prev_values) — ไว้ดูย้อนหลังในแถวที่ยืนยันแล้ว
export function groupPrevValues(prev) {
  const groups = new Map();
  for (const [sku, v] of Object.entries(prev ?? {})) {
    const value = norm(v?.material);
    if (!groups.has(value)) groups.set(value, { value, skus: [] });
    groups.get(value).skus.push(sku);
  }
  return [...groups.values()].sort((a, b) => b.skus.length - a.skus.length);
}

// ช่อง "แหล่งข้อมูลวัสดุ" ในไฟล์ส่งชิปปิ้ง — ข้อความธรรมดา ไม่ใช่ลิงก์
//   prov = { source_tier, approved_edited } จาก getDossier (ยังไม่รัน deploy-38 = ไม่มี → เหลือแค่ลิงก์)
export function materialSourceText(card, prov) {
  return [
    t(card?.material_source_url),
    TIER_LABEL[prov?.source_tier] ?? '',
    prov?.approved_edited ? 'คนแก้ค่าแล้ว' : '',
  ].filter(Boolean).join(' · ');
}
